/**
 * FirstPartyDataRoomPage — the brand's 1st Party Data hub.
 *
 * Replaces FootprintPage.js. Per the design contract
 * (/app/docs/DESIGN_CONTRACT.md), this page is Tier 1: zero internal
 * vocabulary, every panel = headline → why → action, all empty states
 * carry plain-English copy.
 *
 * Six upload cards, drag-and-drop CSV ingestion, hashed PII at the
 * backend, consent toggle gating downstream personalization, hard-delete
 * with audit-row preservation. The backend rules:
 *   /app/backend/routes/first_party_data.py
 *   /app/docs/FIRST_PARTY_DATA_INTEGRATION_AUDIT.md (safe/unsafe surfaces)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Upload, Loader2, Check, Download, Database, ShieldCheck,
  Trash2, AlertTriangle, ChevronRight, Sparkles, Activity,
  Users, BarChart3, MapPin, Network, PenLine, Package,
} from 'lucide-react';
import DashboardLayout from '../layouts/DashboardLayout';
import FootprintCompareStrip from '../components/brand/FootprintCompareStrip';
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

// Card icon + accent per data_type. The benefit lines come from the
// backend's CARD_META so a single source of truth governs the copy.
const CARD_ICONS = {
  customer_segments:      { icon: Users,     accent: '#A78BFA' },
  performance_history:    { icon: BarChart3, accent: '#FFB84D' },
  geographic_performance: { icon: MapPin,    accent: '#4ade80' },
  audience_overlap:       { icon: Network,   accent: '#00F5FF' },
  creative_insights:      { icon: PenLine,   accent: '#FF7AB6' },
  sku_performance:        { icon: Package,   accent: '#9C9EFF' },
};

const HEALTH_COLOR = (score) => {
  if (score >= 80) return { bg: 'rgba(74, 222, 128, 0.18)', text: '#4ade80', label: 'Healthy' };
  if (score >= 50) return { bg: 'rgba(255, 184, 77, 0.18)', text: '#FFB84D', label: 'Building' };
  return { bg: 'rgba(255, 99, 99, 0.18)', text: '#FF6363', label: 'Sparse' };
};


/* ─── One upload card ─────────────────────────────────────────────── */

function UploadCard({ card, onUpload, onReupload, isUploading }) {
  const { icon: Icon, accent } = CARD_ICONS[card.data_type] || { icon: Database, accent: '#888' };
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const handleFile = (file) => { if (file) onUpload(card.data_type, file); };
  const triggerFile = () => fileRef.current?.click();

  return (
    <div
      className="rounded-2xl p-5 transition-all"
      style={{ background: '#0F0F0F', border: `1px solid ${card.connected ? `${accent}30` : '#1E2028'}` }}
      data-testid={`card-${card.data_type}`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        handleFile(e.dataTransfer.files?.[0]);
      }}
    >
      <input type="file" ref={fileRef} hidden accept=".csv,.tsv,.txt"
             onChange={(e) => handleFile(e.target.files?.[0])} />

      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
             style={{ background: `${accent}18`, border: `1px solid ${accent}33` }}>
          <Icon className="h-5 w-5" style={{ color: accent }} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-white">{card.title}</h3>
          <p className="text-[11px] text-gray-400 mt-1 italic leading-snug">{card.benefit}</p>
        </div>
      </div>

      {card.connected ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Check className="h-3.5 w-3.5 text-green-400 flex-shrink-0" />
            <span className="text-[10px] font-bold text-green-400 uppercase tracking-widest">Connected</span>
            <span className="text-[10px] text-gray-600 ml-auto">
              {new Date(card.uploaded_at).toLocaleDateString('en-US',
                { month: 'short', day: 'numeric' })}
            </span>
          </div>
          {card.preview_label && (
            <p className="text-[11px] font-semibold" style={{ color: accent }}>{card.preview_label}</p>
          )}
          <div className="flex items-center gap-2">
            <button
              onClick={triggerFile}
              className="text-[9px] text-gray-500 hover:text-white transition-colors"
              data-testid={`reupload-${card.data_type}`}
            >Re-upload</button>
            <button
              onClick={() => onReupload?.(card.data_type, 'delete')}
              className="text-[9px] text-gray-600 hover:text-red-400 transition-colors ml-auto"
              data-testid={`delete-${card.data_type}`}
            >Delete</button>
          </div>
        </div>
      ) : (
        <>
          <div
            className={`border-2 border-dashed rounded-xl p-4 mb-3 text-center transition-all cursor-pointer ${
              dragOver ? 'border-[#00F5FF]/40 bg-[#00F5FF]/5' : 'border-[#1E2028]'
            }`}
            onClick={triggerFile}
          >
            {isUploading
              ? <Loader2 className="h-5 w-5 text-[#00F5FF] mx-auto mb-1.5 animate-spin" />
              : <Upload className="h-5 w-5 text-gray-600 mx-auto mb-1.5" />}
            <p className="text-[10px] text-gray-600">
              {isUploading ? 'Uploading...' : 'Drag & drop your file here'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={triggerFile}
              disabled={isUploading}
              className="flex-1 px-3 py-2 rounded-xl text-[10.5px] font-bold transition-all flex items-center justify-center gap-1.5 disabled:opacity-50"
              style={{ background: `${accent}15`, border: `1px solid ${accent}30`, color: accent }}
              data-testid={`upload-btn-${card.data_type}`}
            >
              {isUploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
              Upload CSV
            </button>
            <button
              onClick={() => toast('Manual entry — coming soon. Use CSV for now.')}
              className="px-3 py-2 rounded-xl text-[10px] font-semibold text-gray-500 hover:text-gray-300 transition-colors"
              data-testid={`manual-${card.data_type}`}
            >Manual Entry</button>
          </div>
          <a
            href={`${API}/api/brand/data-room/template/${card.data_type}`}
            target="_blank" rel="noopener noreferrer"
            className="text-[9px] text-gray-600 hover:text-[#00F5FF] mt-2 inline-flex items-center gap-1 transition-colors"
            data-testid={`template-${card.data_type}`}
          >
            <Download className="h-2.5 w-2.5" />Download template
          </a>
        </>
      )}
    </div>
  );
}


/* ─── Main page ──────────────────────────────────────────────────── */

export default function FirstPartyDataRoomPage() {
  const [statusData, setStatusData] = useState({ cards: [], last_updated: null, connected_count: 0 });
  const [healthData, setHealthData] = useState({ score: 0, per_type: {} });
  const [uploadingId, setUploadingId] = useState(null);
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [consentOn, setConsentOn] = useState(true);

  const loadAll = useCallback(async () => {
    try {
      const [statusR, healthR] = await Promise.all([
        axios.get(`${API}/api/brand/data-room/status`, { withCredentials: true }),
        axios.get(`${API}/api/brand/data-room/health-score`, { withCredentials: true }),
      ]);
      setStatusData(statusR.data);
      setHealthData(healthR.data);
      // Consent is per-record on the backend; here we treat the toggle
      // as "all active records consented" — if any are revoked, treat
      // the page-level toggle as off.
      const allConsent = (statusR.data.cards || []).every(
        (c) => !c.connected || c.consent_flag !== false
      );
      setConsentOn(allConsent);
    } catch (err) {
      console.warn('Data-room load failed:', err?.message);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const uploadFile = async (dataType, file) => {
    setUploadingId(dataType);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await axios.post(
        `${API}/api/brand/data-room/upload/${dataType}`,
        form,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      toast.success(`${r.data.preview_label || 'Uploaded'} · +${(healthData.per_type?.[dataType]?.weight || 15)} pts`);
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingId(null);
    }
  };

  const deleteOne = async (dataType) => {
    if (!window.confirm(`Delete ${dataType.replace(/_/g, ' ')}? You can re-upload later.`)) return;
    try {
      await axios.delete(`${API}/api/brand/data-room/${dataType}`, { withCredentials: true });
      toast('Removed.');
      await loadAll();
    } catch {
      toast.error('Could not delete that data.');
    }
  };

  const deleteAll = async () => {
    try {
      await axios.delete(`${API}/api/brand/data-room/all`, { withCredentials: true });
      toast('All your 1st party data has been deleted.');
      setConfirmDeleteAll(false);
      await loadAll();
    } catch {
      toast.error('Delete failed. Try again or contact support.');
    }
  };

  const toggleConsent = async (next) => {
    setConsentOn(next);
    try {
      await axios.post(
        `${API}/api/brand/data-room/consent`,
        { enabled: next },
        { withCredentials: true },
      );
      toast(next
        ? 'Matching & orchestration are using your data again.'
        : 'Your data is no longer being used for matching.');
      await loadAll();
    } catch {
      toast.error('Could not update privacy setting.');
      setConsentOn(!next);  // revert
    }
  };

  const lastUpdatedDays = useMemo(() => {
    if (!statusData.last_updated) return null;
    const days = Math.floor((Date.now() - new Date(statusData.last_updated).getTime()) / 86_400_000);
    return days;
  }, [statusData.last_updated]);

  const handler = (dt, action) => { if (action === 'delete') deleteOne(dt); };
  const health = HEALTH_COLOR(healthData.score);

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="data-room-page">

        {/* Hero */}
        <div className="rounded-2xl p-6 flex items-start justify-between gap-6"
             style={{ background: 'linear-gradient(135deg, #0F0F0F, #0A0A0A)',
                      border: '1px solid #1E2028' }}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Database className="h-5 w-5 text-[#A78BFA]" />
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold">
                Private to your brand
              </span>
            </div>
            <h1 className="text-3xl font-black text-white mb-2">1st Party Data Room</h1>
            <p className="text-sm text-gray-400 max-w-2xl">
              Upload your private brand data once. Get smarter creator matching,
              better waves, and higher performance forever.
            </p>
            {lastUpdatedDays !== null && (
              <p className="text-[10px] text-gray-600 mt-3" data-testid="last-updated">
                Last updated: {lastUpdatedDays === 0 ? 'today'
                  : lastUpdatedDays === 1 ? 'yesterday'
                  : `${lastUpdatedDays} days ago`}
              </p>
            )}
          </div>
          <div className="flex items-center gap-4 flex-shrink-0">
            <div
              className="flex flex-col items-center justify-center w-20 h-20 rounded-full"
              style={{ background: health.bg, border: `2px solid ${health.text}40` }}
              data-testid="health-score-badge"
              data-score={healthData.score}
            >
              <div className="text-2xl font-black" style={{ color: health.text }}>
                {healthData.score}
              </div>
              <div className="text-[7px] uppercase tracking-widest font-bold"
                   style={{ color: health.text }}>
                / 100
              </div>
            </div>
            <Button
              className="bg-[#FF4D94] hover:brightness-110 text-white font-bold"
              style={{ boxShadow: '0 0 18px rgba(255,77,148,0.32)' }}
              onClick={() => document.querySelector('[data-testid="card-customer_segments"]')
                                ?.scrollIntoView({ block: 'start', behavior: 'smooth' })}
              data-testid="upload-new-data-cta"
            >
              <Upload className="h-3.5 w-3.5 mr-1.5" />
              Upload new data
            </Button>
          </div>
        </div>

        {/* 6-card grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
             data-testid="data-room-grid">
          {statusData.cards.map((card) => (
            <UploadCard
              key={card.data_type}
              card={card}
              onUpload={uploadFile}
              onReupload={handler}
              isUploading={uploadingId === card.data_type}
            />
          ))}
        </div>

        {/* Investor demo strip — preserved + reframed in the component itself */}
        <FootprintCompareStrip />

        {/* Privacy & controls bar */}
        <div className="rounded-2xl p-5"
             style={{ background: '#0F0F0F', border: '1px solid #1E2028' }}
             data-testid="privacy-controls">
          <div className="flex items-start gap-3 mb-4">
            <ShieldCheck className="h-5 w-5 text-[#4ade80] flex-shrink-0 mt-0.5" />
            <p className="text-[12px] text-gray-300">
              Your data is hashed, never shared, and can be deleted anytime.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <Switch
                checked={consentOn}
                onCheckedChange={toggleConsent}
                data-testid="consent-toggle"
              />
              <div>
                <p className="text-[12px] font-semibold text-white">
                  Use this data for matching & orchestration
                </p>
                <p className="text-[10px] text-gray-500">
                  When off, your data stays uploaded but is not referenced by
                  any creator-matching or caption flow.
                </p>
              </div>
            </div>
            <button
              onClick={() => setConfirmDeleteAll(true)}
              className="px-4 py-2 rounded-xl text-[11px] font-bold text-red-400 hover:bg-red-500/10 border border-red-500/30 flex items-center gap-1.5 transition-colors"
              data-testid="delete-all-button"
            >
              <Trash2 className="h-3 w-3" />Delete all my data
            </button>
          </div>
        </div>

      </div>

      {/* Confirm modal for the destructive action */}
      <Dialog open={confirmDeleteAll} onOpenChange={setConfirmDeleteAll}>
        <DialogContent className="bg-[#0F0F0F] border border-[#FF6363]/30" data-testid="confirm-delete-all-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-400" />
              Delete all 1st party data?
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              This removes all six uploads from active use. Your audit row
              (when and what was uploaded) is preserved for compliance, but
              the data itself is purged. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline"
                    onClick={() => setConfirmDeleteAll(false)}
                    data-testid="cancel-delete-all">Cancel</Button>
            <Button
              className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/40"
              onClick={deleteAll}
              data-testid="confirm-delete-all"
            >
              Yes, delete everything
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
